import React, { useState, useEffect } from 'react';
import { FileText, Search, Download, CheckCircle, AlertTriangle, Loader2 } from 'lucide-react';
import api from '../../services/api';

const fmtINR = (v) => '₹' + (v || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 });

const InvoiceReconciliationReport = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('all'); // all, invoiced, pending

  const loadData = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filter !== 'all') params.status = filter;
      if (search) params.search = search;
      const r = await api.get('/order-confirmations/reports/invoice-reconciliation', { params });
      setData(r.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  useEffect(() => { loadData(); }, [filter, search]);

  const invoiced = data.filter(d => d.tally_invoice_number);
  const pending = data.filter(d => !d.tally_invoice_number);
  const totalValue = data.reduce((s, d) => s + (d.total || 0), 0);
  const invoicedValue = invoiced.reduce((s, d) => s + (d.total || 0), 0);

  const exportCSV = () => {
    const headers = ['OC Number', 'Customer', 'OC Type', 'Project', 'Invoice Number', 'Invoice Date', 'Total Value', 'Status', 'Salesman', 'Created'];
    const rows = data.map(d => [
      d.oc_number || '', d.customer?.name || '', d.oc_type || '', d.project_name || '',
      d.tally_invoice_number || '', d.tally_invoice_date || '', d.total || 0, d.status || '',
      d.salesman_name || '', d.created_at?.slice(0, 10) || ''
    ]);
    const csv = [headers, ...rows].map(r => r.map(c => `"${c}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url; a.download = 'OC_Invoice_Reconciliation.csv';
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
  };

  return (
    <div className="space-y-4" data-testid="invoice-reconciliation-report">
      {/* Summary Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <div className="bg-white rounded-xl border border-gray-100 p-3">
          <p className="text-[10px] font-semibold text-gray-400 uppercase">Total OCs</p>
          <p className="text-xl font-bold text-gray-900">{data.length}</p>
          <p className="text-[10px] text-gray-400 mt-0.5">Value: {fmtINR(totalValue)}</p>
        </div>
        <div className="bg-white rounded-xl border border-gray-100 p-3">
          <p className="text-[10px] font-semibold text-green-500 uppercase">Invoiced</p>
          <p className="text-xl font-bold text-green-600">{invoiced.length}</p>
          <p className="text-[10px] text-gray-400 mt-0.5">Value: {fmtINR(invoicedValue)}</p>
        </div>
        <div className="bg-white rounded-xl border border-gray-100 p-3">
          <p className="text-[10px] font-semibold text-amber-500 uppercase">Pending Invoice</p>
          <p className="text-xl font-bold text-amber-600">{pending.length}</p>
          <p className="text-[10px] text-gray-400 mt-0.5">Value: {fmtINR(totalValue - invoicedValue)}</p>
        </div>
        <div className="bg-white rounded-xl border border-gray-100 p-3">
          <p className="text-[10px] font-semibold text-blue-500 uppercase">Reconciliation %</p>
          <p className="text-xl font-bold" style={{ color: '#24549c' }}>{data.length > 0 ? Math.round(invoiced.length / data.length * 100) : 0}%</p>
          <p className="text-[10px] text-gray-400 mt-0.5">{invoiced.length} of {data.length} matched</p>
        </div>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-3 flex-wrap">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
          <input data-testid="reconciliation-search" value={search} onChange={e => setSearch(e.target.value)} placeholder="Search OC #, Invoice #, Customer..." className="w-full pl-9 pr-3 py-2 text-xs border border-gray-200 rounded-lg" />
        </div>
        <div className="flex gap-1">
          {[{ v: 'all', l: 'All' }, { v: 'invoiced', l: 'Invoiced' }, { v: 'pending', l: 'Pending' }].map(f => (
            <button key={f.v} data-testid={`filter-${f.v}`} onClick={() => setFilter(f.v)}
              className={`px-3 py-1.5 text-[11px] font-medium rounded-full transition-all ${filter === f.v ? 'text-white' : 'text-gray-600 bg-gray-100 hover:bg-gray-200'}`}
              style={filter === f.v ? { backgroundColor: '#24549c' } : {}}>
              {f.l}
            </button>
          ))}
        </div>
        <button onClick={exportCSV} className="flex items-center gap-1.5 px-3 py-1.5 text-[11px] font-semibold text-green-700 bg-green-50 rounded-lg hover:bg-green-100 border border-green-100 ml-auto">
          <Download className="w-3 h-3" />Export CSV
        </button>
      </div>

      {/* Table */}
      {loading ? (
        <div className="flex justify-center py-12"><Loader2 className="w-5 h-5 animate-spin text-blue-600" /></div>
      ) : (
        <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead><tr className="bg-gray-50">
                {['OC Number', 'Customer', 'Type', 'Project', 'Invoice #', 'Invoice Date', 'Total', 'OC Status', 'Salesman'].map(h => (
                  <th key={h} className="text-left px-3 py-2.5 text-[10px] font-semibold text-gray-500 uppercase">{h}</th>
                ))}
              </tr></thead>
              <tbody>{data.map((d, i) => (
                <tr key={i} className={`border-t border-gray-50 hover:bg-gray-50/50 ${!d.tally_invoice_number ? 'bg-amber-50/20' : ''}`}>
                  <td className="px-3 py-2 font-mono font-semibold text-gray-900">{d.oc_number}</td>
                  <td className="px-3 py-2 font-medium">{d.customer?.name || '-'}</td>
                  <td className="px-3 py-2"><span className={`px-1.5 py-0.5 text-[9px] font-bold rounded-full ${d.oc_type === 'project' ? 'bg-blue-50 text-blue-600' : d.oc_type === 'retail' ? 'bg-purple-50 text-purple-600' : 'bg-gray-100 text-gray-600'}`}>{d.oc_type || 'generic'}</span></td>
                  <td className="px-3 py-2 text-gray-600">{d.project_name || '-'}</td>
                  <td className="px-3 py-2">
                    {d.tally_invoice_number ? (
                      <span className="flex items-center gap-1 font-mono text-green-600"><CheckCircle className="w-3 h-3" />{d.tally_invoice_number}</span>
                    ) : (
                      <span className="flex items-center gap-1 text-amber-500"><AlertTriangle className="w-3 h-3" />Pending</span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-gray-500">{d.tally_invoice_date || '-'}</td>
                  <td className="px-3 py-2 font-bold" style={{ color: '#24549c' }}>{fmtINR(d.total)}</td>
                  <td className="px-3 py-2"><span className={`px-1.5 py-0.5 text-[9px] font-bold rounded-full ${d.status === 'Dispatched' ? 'bg-blue-100 text-blue-700' : d.status === 'Confirmed' ? 'bg-green-100 text-green-700' : d.status === 'Cancelled' ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'}`}>{d.status}</span></td>
                  <td className="px-3 py-2 text-gray-500">{d.salesman_name || '-'}</td>
                </tr>
              ))}</tbody>
            </table>
          </div>
          <div className="px-4 py-2 border-t border-gray-100 text-[10px] text-gray-500">
            {data.length} records | Total: {fmtINR(totalValue)} | Invoiced: {fmtINR(invoicedValue)} | Pending: {fmtINR(totalValue - invoicedValue)}
          </div>
        </div>
      )}
    </div>
  );
};

export default InvoiceReconciliationReport;
